/**
 * Minimal Metaplex Token Metadata helper — hand-rolled Borsh so we don't pull
 * in the full `@metaplex-foundation/mpl-token-metadata` package (umi + heavy
 * deps). Only needs CreateMetadataAccountV3 which is what Phantom/Solscan
 * read to display name, symbol, and logo.
 *
 * Spec: https://developers.metaplex.com/token-metadata/token-standard
 */

import {
  PublicKey,
  SystemProgram,
  SYSVAR_RENT_PUBKEY,
  TransactionInstruction,
} from "@solana/web3.js";

export const TOKEN_METADATA_PROGRAM_ID = new PublicKey(
  "metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s"
);

/** ["metadata", metadata_program, mint] — deterministic per-mint PDA. */
export function deriveMetadataPda(mint) {
  const [pda] = PublicKey.findProgramAddressSync(
    [
      Buffer.from("metadata"),
      TOKEN_METADATA_PROGRAM_ID.toBuffer(),
      mint.toBuffer(),
    ],
    TOKEN_METADATA_PROGRAM_ID
  );
  return pda;
}

// ─── Borsh primitives ──────────────────────────────────────────────────────

function writeU8(buf, value) {
  buf.push(value & 0xff);
}
function writeU16(buf, value) {
  buf.push(value & 0xff, (value >> 8) & 0xff);
}
function writeU32(buf, value) {
  buf.push(
    value & 0xff,
    (value >> 8) & 0xff,
    (value >> 16) & 0xff,
    (value >> 24) & 0xff
  );
}
function writeBool(buf, value) {
  writeU8(buf, value ? 1 : 0);
}
function writeString(buf, s) {
  const bytes = Buffer.from(s ?? "", "utf8");
  writeU32(buf, bytes.length);
  for (const b of bytes) buf.push(b);
}
/** Borsh `Option<T>`: 0 = None, 1 = Some + inner. */
function writeOption(buf, value, writer) {
  if (value == null) {
    writeU8(buf, 0);
  } else {
    writeU8(buf, 1);
    writer(buf, value);
  }
}

// ─── Instruction builder ───────────────────────────────────────────────────

/**
 * Build the CreateMetadataAccountV3 instruction.
 *
 * @param {object} args
 * @param {PublicKey} args.metadata   - Metadata PDA (derive first)
 * @param {PublicKey} args.mint
 * @param {PublicKey} args.mintAuthority
 * @param {PublicKey} args.payer
 * @param {PublicKey} args.updateAuthority
 * @param {string}    args.name
 * @param {string}    args.symbol
 * @param {string}    args.uri       - URL to the metadata JSON
 * @param {number}   [args.sellerFeeBasisPoints=0]
 */
export function createCreateMetadataV3Instruction({
  metadata,
  mint,
  mintAuthority,
  payer,
  updateAuthority,
  name,
  symbol,
  uri,
  sellerFeeBasisPoints = 0,
}) {
  const data = [];
  // Discriminator for CreateMetadataAccountV3
  writeU8(data, 33);

  // DataV2 { name, symbol, uri, seller_fee_basis_points, creators?, collection?, uses? }
  writeString(data, name);
  writeString(data, symbol);
  writeString(data, uri);
  writeU16(data, sellerFeeBasisPoints);
  writeOption(data, null); // creators
  writeOption(data, null); // collection
  writeOption(data, null); // uses

  // is_mutable, collection_details
  // Lock metadata at creation — name, symbol, and URI become permanent the
  // moment the launch tx confirms. Creator never gains the ability to rename
  // their token to "OFFICIAL BTC ETF" after launch. The Metaplex update
  // authority is moot once is_mutable is false: no path through the program
  // can change metadata fields, regardless of who holds the auth.
  writeBool(data, false);
  writeOption(data, null); // collection_details

  return new TransactionInstruction({
    programId: TOKEN_METADATA_PROGRAM_ID,
    keys: [
      { pubkey: metadata,         isSigner: false, isWritable: true  },
      { pubkey: mint,             isSigner: false, isWritable: false },
      { pubkey: mintAuthority,    isSigner: true,  isWritable: false },
      { pubkey: payer,            isSigner: true,  isWritable: true  },
      { pubkey: updateAuthority,  isSigner: false, isWritable: false },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: SYSVAR_RENT_PUBKEY,      isSigner: false, isWritable: false },
    ],
    data: Buffer.from(data),
  });
}
